import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type FocusInputView, type FocusView } from "../../lib/api";

/**
 * Pipeline focus — "give this niche N% of the work".
 *
 * A focus is a set of methods (or clusters, or families) plus a share. The
 * autopilot deals its slots by share, the enrichment queue spends searches by
 * share, and the social planner fills each week by share. Whatever is not
 * taken goes to the normal queue, and a focus with nothing ready gives its
 * slots back rather than leaving a run short.
 */

const LANE_COLORS = ["bg-teal-600", "bg-indigo-500", "bg-amber-500", "bg-rose-500"];

const EMPTY: FocusInputView = { label: "", methods: [], clusterIds: [], families: [], share: 30, active: true, endsAt: null };

export function FocusPanel() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ["focus"], queryFn: () => api.getFocus() });
  const [draft, setDraft] = useState<FocusInputView | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["focus"] });
  };
  const save = useMutation({
    mutationFn: (f: FocusInputView) => api.saveFocus(f),
    onSuccess: () => { setDraft(null); setErr(null); refresh(); },
    onError: (e: Error) => setErr(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteFocus(id),
    onSuccess: refresh,
    onError: (e: Error) => setErr(e.message),
  });

  const foci = data.data?.foci ?? [];
  const activeIds = new Set(data.data?.activeIds ?? []);
  const active = foci.filter((f) => activeIds.has(f.id));
  const free = data.data?.freeShare ?? 100;
  const names = useMemo(
    () => new Map((data.data?.options.methods ?? []).map((m) => [m.slug, m.nameHe])),
    [data.data],
  );

  const toInput = (f: FocusView): FocusInputView => ({
    id: f.id, label: f.label, methods: f.methods, clusterIds: f.clusterIds, families: f.families,
    share: f.share, active: f.active, endsAt: f.endsAt,
  });

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Focus</h2>
          <p className="mt-0.5 max-w-3xl text-xs text-slate-600">
            Give a niche a share of the work. The autopilot, the enrichment queue and the social planner all deal
            their slots by these shares; the rest goes to the normal queue. A focus with nothing ready gives its
            slots back, so a run is never left short.
          </p>
        </div>
        {!draft && (
          <button
            type="button"
            onClick={() => setDraft({ ...EMPTY, share: Math.min(EMPTY.share, Math.max(1, free)) })}
            disabled={free <= 0}
            className="rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:bg-slate-300"
            title={free <= 0 ? "Active foci already take 100%" : undefined}
          >
            New focus
          </button>
        )}
      </div>

      {/* Share bar: what every engine's work looks like right now. */}
      <div className="mt-3 flex h-6 overflow-hidden rounded border border-slate-200 text-[11px] font-medium" role="img"
        aria-label={`${active.map((f) => `${f.label} ${f.share}%`).join(", ")}${active.length ? ", " : ""}everything else ${free}%`}>
        {active.map((f, i) => (
          <div key={f.id} className={`flex items-center justify-center text-white ${LANE_COLORS[i % LANE_COLORS.length]}`}
            style={{ width: `${f.share}%` }} dir="auto">
            {f.label} · {f.share}%
          </div>
        ))}
        {free > 0 && (
          <div className="flex items-center justify-center bg-slate-100 text-slate-600" style={{ width: `${free}%` }}>
            everything else · {free}%
          </div>
        )}
      </div>

      {err && <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}

      {foci.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100 rounded border border-slate-200">
          {foci.map((f) => {
            const on = activeIds.has(f.id);
            const ended = f.endsAt && f.endsAt <= new Date().toISOString();
            return (
              <li key={f.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs">
                <span className={`rounded-full px-2 py-0.5 font-medium ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {on ? "on" : ended ? "ended" : "off"}
                </span>
                <span className="font-semibold text-slate-900" dir="auto">{f.label}</span>
                <span className="tabular-nums text-slate-700">{f.share}%</span>
                <span className="min-w-0 flex-1 truncate text-slate-500" dir="rtl">
                  {[...f.methods.map((m) => names.get(m) ?? m), ...f.families, ...f.clusterIds].join(" · ")}
                </span>
                {f.endsAt && <span className="text-slate-500">until {f.endsAt.slice(0, 10)}</span>}
                <button type="button" className="text-slate-600 underline" onClick={() => save.mutate({ ...toInput(f), active: !f.active })}>
                  {f.active ? "Pause" : "Resume"}
                </button>
                <button type="button" className="text-slate-600 underline" onClick={() => setDraft(toInput(f))}>Edit</button>
                <button type="button" className="text-red-600 underline"
                  onClick={() => { if (window.confirm?.(`Delete focus "${f.label}"?`) !== false) remove.mutate(f.id); }}>
                  Delete
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {draft && data.data && (
        <FocusEditor
          draft={draft}
          setDraft={setDraft}
          options={data.data.options}
          maxShare={free + (draft.id ? foci.find((f) => f.id === draft.id && activeIds.has(f.id))?.share ?? 0 : 0)}
          saving={save.isPending}
          onSave={() => save.mutate(draft)}
          onCancel={() => { setDraft(null); setErr(null); }}
        />
      )}

      <FocusPreviewBlock />
    </section>
  );
}

function FocusEditor(props: {
  draft: FocusInputView;
  setDraft: (d: FocusInputView) => void;
  options: NonNullable<Awaited<ReturnType<typeof api.getFocus>>>["options"];
  maxShare: number;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { draft, setDraft, options } = props;
  const [q, setQ] = useState("");
  const chosen = new Set(draft.methods);
  const toggle = (slug: string) =>
    setDraft({ ...draft, methods: chosen.has(slug) ? draft.methods.filter((m) => m !== slug) : [...draft.methods, slug] });
  const shown = options.methods.filter((m) => !q || m.nameHe.includes(q) || m.slug.includes(q.toLowerCase()));
  const max = Math.max(1, props.maxShare);
  const empty = draft.methods.length + draft.families.length + draft.clusterIds.length === 0;

  return (
    <div className="mt-3 space-y-3 rounded border border-slate-300 bg-slate-50 p-3 text-xs">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="font-medium text-slate-700">Name</span>
          <input id="focus-label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            placeholder="עיסוי" dir="auto" className="w-48 rounded border border-slate-300 px-2 py-1" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-medium text-slate-700">Share of the work: {Math.min(draft.share, max)}%</span>
          <input id="focus-share" type="range" min={1} max={max} value={Math.min(draft.share, max)}
            onChange={(e) => setDraft({ ...draft, share: Number(e.target.value) })} className="w-56" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-medium text-slate-700">Ends (optional)</span>
          <input id="focus-ends" type="date" value={draft.endsAt?.slice(0, 10) ?? ""}
            onChange={(e) => setDraft({ ...draft, endsAt: e.target.value ? `${e.target.value}T23:59:59.000Z` : null })}
            className="rounded border border-slate-300 px-2 py-1" />
        </label>
      </div>

      <div>
        <div className="mb-1 font-medium text-slate-700">Quick picks</div>
        <div className="flex flex-wrap gap-2">
          {options.presets.map((p) => (
            <button key={p.id} type="button" dir="rtl"
              onClick={() => setDraft({
                ...draft,
                label: draft.label || p.labelHe.replace(/\s*\(.*\)$/, ""),
                methods: [...new Set([...draft.methods, ...p.methods])],
              })}
              className="rounded border border-teal-300 bg-white px-2 py-1 text-teal-800 hover:bg-teal-50">
              + {p.labelHe}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center gap-2">
          <span className="font-medium text-slate-700">Methods</span>
          <span className="text-slate-500">{draft.methods.length} chosen · number = open keywords</span>
          <input id="focus-method-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search…" dir="auto"
            className="ml-auto w-40 rounded border border-slate-300 px-2 py-0.5" />
        </div>
        <div className="grid max-h-48 grid-cols-2 gap-x-3 gap-y-1 overflow-y-auto rounded border border-slate-200 bg-white p-2 sm:grid-cols-3 lg:grid-cols-4" dir="rtl">
          {shown.map((m) => (
            <label key={m.slug} className="flex min-w-0 items-center gap-1.5">
              <input type="checkbox" checked={chosen.has(m.slug)} onChange={() => toggle(m.slug)} />
              <span className="truncate">{m.nameHe}</span>
              <span className="tabular-nums text-slate-400">{m.openKeywords}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1 font-medium text-slate-700">Whole families (optional)</div>
        <div className="flex flex-wrap gap-3" dir="rtl">
          {options.families.map((f) => (
            <label key={f.id} className="flex items-center gap-1.5">
              <input type="checkbox" checked={draft.families.includes(f.id)}
                onChange={() => setDraft({
                  ...draft,
                  families: draft.families.includes(f.id) ? draft.families.filter((x) => x !== f.id) : [...draft.families, f.id],
                })} />
              {f.labelHe}
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button type="button" onClick={props.onSave} disabled={props.saving || empty || !draft.label.trim()}
          className="rounded bg-slate-900 px-3 py-1.5 font-medium text-white disabled:bg-slate-300">
          {props.saving ? "Saving…" : "Save focus"}
        </button>
        <button type="button" onClick={props.onCancel} className="px-2 py-1.5 text-slate-600 underline">Cancel</button>
        {empty && <span className="text-slate-500">Pick at least one method or family.</span>}
      </div>
    </div>
  );
}

function FocusPreviewBlock() {
  const qc = useQueryClient();
  const p = useQuery({ queryKey: ["focus", "preview"], queryFn: () => api.getFocusPreview(), staleTime: 60_000 });
  if (p.isLoading) return <p className="mt-3 text-xs text-slate-500">Measuring what the foci do to the next runs…</p>;
  if (!p.data?.ok) return p.data?.blocked ? <p className="mt-3 text-xs text-amber-700">{p.data.blocked}</p> : null;
  const lanes = p.data.lanes ?? [];
  if (lanes.length <= 1) return null;

  return (
    <div className="mt-4">
      <div className="mb-1 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">What this does right now</h3>
        <button type="button" className="text-xs text-slate-500 underline"
          onClick={() => void qc.invalidateQueries({ queryKey: ["focus", "preview"] })}>
          {p.isFetching ? "measuring…" : "refresh"}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1 pr-3 font-medium">Lane</th>
              <th className="py-1 pr-3 font-medium">Share</th>
              <th className="py-1 pr-3 font-medium">Ready to write</th>
              <th className="py-1 pr-3 font-medium">Waiting for enrichment</th>
              <th className="py-1 pr-3 font-medium">Next autopilot run (of {p.data.maxArticles})</th>
              <th className="py-1 pr-3 font-medium">Next 100 enrichment searches</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {lanes.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="py-1 pr-3 font-medium text-slate-800" dir="auto">{l.label}</td>
                <td className="py-1 pr-3">{l.share}%</td>
                <td className="py-1 pr-3">
                  {l.readyNow}
                  {l.id !== "general" && l.readyNow === 0 && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                      nothing ready — enrich first
                    </span>
                  )}
                </td>
                <td className="py-1 pr-3">{l.waitingForEnrichment}</td>
                <td className="py-1 pr-3">{l.inNextRun}</td>
                <td className="py-1 pr-3">{l.inNextEnrichment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(p.data.nextRun?.length ?? 0) > 0 && (
        <p className="mt-2 text-xs text-slate-600" dir="rtl">
          <span className="font-medium">הריצה הבאה: </span>
          {p.data.nextRun!.map((r) => (r.focus ? `${r.keyword} (${r.focus})` : r.keyword)).join(" · ")}
        </p>
      )}
    </div>
  );
}
